import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AnimalItem, ShareItem, DurumTuru } from "../../../../../mocks/animals";
import { buildGroupedHisseNo, getShareSlotNos } from "../../../../../lib/salesFlow";
import { useAnimals } from "../../../../../hooks/useAnimals";
import { useCustomers } from "../../../../../hooks/useCustomers";
import { usePayments } from "../../../../../hooks/usePayments";
import {
  analyzeShareSaleDeletion,
  deleteShareSaleCascade,
} from "../../../../../lib/cascadeOps";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../../lib/animalLabels";
import {
  getDeliveryAssignments,
  setAllDeliveryAssignments,
} from "../../../../../lib/deliveryStore";
import SharePaymentDrawer from "../../../hayvanlar/detay/components/SharePaymentDrawer";
import ShareTransferModal from "../../../../../components/transfer/ShareTransferModal";
import MoneyInput from "../../../../../components/form/MoneyInput";
import { formatTry, parseMoneyInput } from "../../../../../lib/money";
import BackdropCloseContainer from "../../../../../components/ui/BackdropCloseContainer";
import { applyOperationResult } from "../../../../../lib/operationRefresh";
import { deleteSaleOperationRequest } from "../../../../../lib/api";

export interface LinkedShare {
  share: ShareItem;
  animalId: number;
  kupeNo: string;
  siparisNo: string;
  kesimGunu?: string;
  hisseliSatisEtiketi?: boolean;
  animalTur: "Büyükbaş" | "Küçükbaş";
  animalDurum: DurumTuru;
}

export interface SaleEditPayload {
  updates: Partial<ShareItem>;
  kaporaTutar: number;
  kaporaTip: "Nakit" | "Havale";
  kaporaDekontNo: string;
  kaporaAciklama: string;
}

interface Props {
  linked: LinkedShare;
  customerId: number;
  onEditSave: (shareId: number, payload: SaleEditPayload) => void;
}

const finansBadge: Record<string, string> = {
  Tam: "bg-farm-green-pale text-farm-green",
  Kısmi: "bg-farm-gold-pale text-farm-gold",
  Bekliyor: "bg-farm-earth-pale text-farm-earth",
};

const opsColor: Record<DurumTuru, string> = {
  Aktif: "bg-farm-green-pale/60 text-farm-green",
  "Kesim Bekliyor": "bg-farm-gold-pale text-farm-gold",
  Kesildi: "bg-farm-earth-pale text-farm-earth",
  "Teslim Edildi": "bg-farm-border/40 text-farm-muted",
};

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className="font-body text-[10px] text-farm-muted leading-none whitespace-nowrap">
        {label}
      </p>
      <p className="font-body text-xs font-semibold leading-none whitespace-nowrap text-farm-brown">
        {value}
      </p>
    </div>
  );
}

function EditPanel({
  share,
  animal,
  kaporaTutar,
  kaporaTip,
  kaporaDekontNo,
  kaporaAciklama,
  onSave,
  onClose,
}: {
  share: ShareItem;
  animal: AnimalItem;
  kaporaTutar: number;
  kaporaTip: "Nakit" | "Havale";
  kaporaDekontNo: string;
  kaporaAciklama: string;
  onSave: (payload: SaleEditPayload) => void;
  onClose: () => void;
}) {
  const [selectedSlots, setSelectedSlots] = useState<number[]>(() => getShareSlotNos(share));

  const [form, setForm] = useState({
    tutar: String(Math.max(0, share.tutar)),
    kesimUcreti: String(Math.max(0, share.kesimUcreti ?? 0)),
    parcalamaUcreti: String(Math.max(0, share.parcalamaUcreti ?? 0)),
    tasimaUcreti: String(Math.max(0, share.tasimaUcreti ?? 0)),
    tasimaVar: Boolean(share.tasimaVar ?? (share.tasimaUcreti ?? 0) > 0),
    indirim: String(Math.max(0, share.indirim ?? 0)),
    satisNotu: share.satisNotu ?? "",
    satisFisNo: share.satisFisNo ?? "",
    kaporaTutar: String(Math.max(0, kaporaTutar)),
    kaporaTip,
    kaporaDekontNo,
    kaporaAciklama,
  });

  const usedByOthers = new Set<number>();
  for (const item of animal.hisseler ?? []) {
    if (item.id === share.id) continue;
    for (const slot of getShareSlotNos(item)) usedByOthers.add(slot);
  }

  const selectableSlots: number[] = [];
  for (let slotNo = 1; slotNo <= animal.toplamHisse; slotNo++) {
    if (!usedByOthers.has(slotNo)) selectableSlots.push(slotNo);
  }

  function toggleSlot(slotNo: number) {
    setSelectedSlots((prev) =>
      prev.includes(slotNo)
        ? prev.filter((item) => item !== slotNo)
        : [...prev, slotNo].sort((a, b) => a - b)
    );
  }

  const toplam =
    parseMoneyInput(form.tutar) +
    parseMoneyInput(form.kesimUcreti) +
    parseMoneyInput(form.parcalamaUcreti) +
    parseMoneyInput(form.tasimaUcreti) -
    parseMoneyInput(form.indirim);

  const yeniKapora = Math.max(0, parseMoneyInput(form.kaporaTutar));
  const oncekiKapora = Math.max(0, kaporaTutar);
  const digerOdemeler = Math.max(0, share.odenen - oncekiKapora);
  const yeniOdenen = digerOdemeler + yeniKapora;
  const bakiye = toplam - yeniOdenen;

  function handleSave() {
    if (selectedSlots.length === 0) return;
    if (toplam < 0) return;
    if (form.kaporaTip === "Havale" && yeniKapora > 0 && !form.kaporaDekontNo.trim()) return;

    const sortedSlots = [...selectedSlots].sort((a, b) => a - b);

    onSave({
      updates: {
        slotNos: sortedSlots,
        hisseNo: buildGroupedHisseNo(animal.siraNo, sortedSlots),
        hisseSayisi: sortedSlots.length,
        tutar: parseMoneyInput(form.tutar),
        kesimUcreti: parseMoneyInput(form.kesimUcreti),
        parcalamaUcreti: parseMoneyInput(form.parcalamaUcreti),
        tasimaUcreti: parseMoneyInput(form.tasimaUcreti),
        tasimaVar: form.tasimaVar,
        indirim: parseMoneyInput(form.indirim),
        satisNotu: form.satisNotu.trim() || undefined,
        satisFisNo: form.satisFisNo.trim() || undefined,
        pesinatAciklama: form.kaporaAciklama.trim() || undefined,
        pesinatDekontNo: form.kaporaDekontNo.trim() || undefined,
      },
      kaporaTutar: yeniKapora,
      kaporaTip: form.kaporaTip,
      kaporaDekontNo: form.kaporaDekontNo.trim(),
      kaporaAciklama: form.kaporaAciklama.trim(),
    });
    onClose();
  }

  return (
    <BackdropCloseContainer
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm px-0 sm:px-4"
    >
      <div className="w-full sm:max-w-xl bg-farm-warm border border-farm-border rounded-t-farm sm:rounded-farm shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border">
          <p className="font-heading font-semibold text-sm text-farm-brown">
            Satışı Düzenle — {share.hisseNo}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-farm-muted hover:text-farm-brown cursor-pointer rounded-full hover:bg-farm-cream transition-colors"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="px-4 py-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
          <div className="rounded-farm border border-farm-border bg-white/70 px-3 py-3">
            <p className="mb-2 font-body text-[10px] font-semibold uppercase tracking-wide text-farm-muted">Hisse Seçimi</p>
            <div className="flex flex-wrap gap-2">
              {selectableSlots.map((slotNo) => {
                const active = selectedSlots.includes(slotNo);
                return (
                  <button
                    key={slotNo}
                    type="button"
                    onClick={() => toggleSlot(slotNo)}
                    className={
                      active
                        ? "rounded-full border border-farm-earth bg-farm-earth px-3 py-1.5 text-xs font-semibold text-white"
                        : "rounded-full border border-farm-border bg-farm-cream px-3 py-1.5 text-xs font-semibold text-farm-brown"
                    }
                  >
                    Hisse {slotNo}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 font-body text-[11px] text-farm-muted">
              Seçili: <span className="font-semibold text-farm-brown">{selectedSlots.join(", ") || "—"}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Toplam Satış Tutarı
              </label>
              <MoneyInput formatWhileTyping value={form.tutar} onChange={(v) => setForm((p) => ({ ...p, tutar: v }))} />
            </div>
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Fiş No
              </label>
              <input
                type="text"
                value={form.satisFisNo}
                onChange={(e) => setForm((p) => ({ ...p, satisFisNo: e.target.value }))}
                className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="rounded-farm border border-farm-border bg-white/70 px-3 py-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide">
                  Taşıma Durumu
                </p>
                <p className="font-body text-xs text-farm-muted mt-0.5">
                  Satış kaydında taşıma hizmeti işaretli mi?
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, tasimaVar: !p.tasimaVar }))}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
                  form.tasimaVar
                    ? "bg-farm-earth text-white border-farm-earth"
                    : "bg-farm-cream text-farm-brown border-farm-border"
                }`}
              >
                <i className={form.tasimaVar ? "ri-truck-line" : "ri-close-circle-line"} />
                {form.tasimaVar ? "Taşıma Var" : "Taşıma Yok"}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                  Kapora Tutarı
                </label>
                <MoneyInput formatWhileTyping value={form.kaporaTutar} onChange={(v) => setForm((p) => ({ ...p, kaporaTutar: v }))} />
              </div>
              <div>
                <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                  Kapora Tipi
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(["Nakit", "Havale"] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, kaporaTip: item }))}
                      className={`h-10 rounded-farm border text-xs font-semibold transition-colors ${
                        form.kaporaTip === item
                          ? item === "Nakit"
                            ? "bg-farm-green text-white border-farm-green"
                            : "bg-sky-600 text-white border-sky-600"
                          : "bg-farm-cream text-farm-brown border-farm-border"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                  Havale / Dekont No
                </label>
                <input
                  type="text"
                  value={form.kaporaDekontNo}
                  onChange={(e) => setForm((p) => ({ ...p, kaporaDekontNo: e.target.value }))}
                  placeholder="Kapora dekont / havale no"
                  className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                  Kapora Açıklama
                </label>
                <input
                  type="text"
                  value={form.kaporaAciklama}
                  onChange={(e) => setForm((p) => ({ ...p, kaporaAciklama: e.target.value }))}
                  placeholder="Satış anındaki ilk ödeme notu"
                  className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Toplam Kesim
              </label>
              <MoneyInput formatWhileTyping value={form.kesimUcreti} onChange={(v) => setForm((p) => ({ ...p, kesimUcreti: v }))} />
            </div>
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Toplam Parçalama
              </label>
              <MoneyInput formatWhileTyping value={form.parcalamaUcreti} onChange={(v) => setForm((p) => ({ ...p, parcalamaUcreti: v }))} />
            </div>
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Toplam Taşıma
              </label>
              <MoneyInput formatWhileTyping value={form.tasimaUcreti} onChange={(v) => setForm((p) => ({ ...p, tasimaUcreti: v }))} />
            </div>
            <div>
              <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
                Toplam İndirim
              </label>
              <MoneyInput formatWhileTyping value={form.indirim} onChange={(v) => setForm((p) => ({ ...p, indirim: v }))} />
            </div>
          </div>

          <div>
            <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
              Satış Notu
            </label>
            <textarea
              value={form.satisNotu}
              onChange={(e) => setForm((p) => ({ ...p, satisNotu: e.target.value }))}
              rows={3}
              className="w-full resize-none bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
            />
          </div>

          <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-farm-muted">Kapora + Diğer Ödemeler</span>
              <span className="font-semibold text-farm-green">{formatTry(yeniOdenen)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-farm-muted">Sadece Kapora</span>
              <span className="font-semibold text-farm-earth">{formatTry(yeniKapora)}</span>
            </div>
            <div className="flex items-center justify-between text-sm border-t border-farm-border/70 pt-2">
              <span className="font-semibold text-farm-brown">Yeni Genel Toplam</span>
              <span className="font-bold text-farm-earth">{formatTry(toplam)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-farm-brown">Kalan / Bakiye</span>
              <span className={`font-bold ${bakiye < 0 ? "text-red-600" : bakiye > 0 ? "text-farm-earth" : "text-farm-green"}`}>
                {formatTry(bakiye)}
              </span>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={selectedSlots.length === 0 || toplam < 0 || (form.kaporaTip === "Havale" && yeniKapora > 0 && !form.kaporaDekontNo.trim())}
              className="flex-1 py-2 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Kaydet
            </button>
          </div>
        </div>
      </div>
    </BackdropCloseContainer>
  );
}

export default function SaleCard({ linked, customerId, onEditSave }: Props) {
  const navigate = useNavigate();
  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();

  const {
    share,
    animalId,
    kupeNo,
    siparisNo,
    kesimGunu,
    hisseliSatisEtiketi,
    animalTur,
    animalDurum,
  } = linked;

  const [showEdit, setShowEdit] = useState(false);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const sharePayments = useMemo(() => {
    return payments
      .filter((payment) => payment.shareId === share.id)
      .sort((a, b) => {
        if (a.tarih === b.tarih) return a.id - b.id;
        return a.tarih.localeCompare(b.tarih);
      });
  }, [payments, share.id]);

  const kaporaPayment = sharePayments[0] ?? null;

  const ownerAnimal = useMemo(() => animals.find((item) => item.id === animalId) ?? null, [animalId, animals]);
  const slotNos = useMemo(() => getShareSlotNos(share), [share]);

  const toplamUcret =
    share.tutar +
    (share.kesimUcreti ?? 0) +
    (share.parcalamaUcreti ?? 0) +
    (share.tasimaUcreti ?? 0) -
    (share.indirim ?? 0);

  const hasFees =
    share.kesimUcreti !== undefined ||
    share.parcalamaUcreti !== undefined ||
    share.tasimaUcreti !== undefined ||
    share.indirim !== undefined;

  async function handleDeleteSale() {
    const analysis = analyzeShareSaleDeletion({
      shareId: share.id,
      animals,
      payments,
    });

    if (!analysis.share || !analysis.animal) {
      window.alert("İptal edilecek hisse satışı bulunamadı.");
      return;
    }

    const ok = window.confirm(
      [
        `${analysis.share.hisseNo} satışı iptal edilsin mi?`,
        `Müşteri: ${analysis.share.musteriAdi}`,
        `Küpe No: ${getDisplayKupeNo(analysis.animal)}`,
        `Sipariş No: ${getDisplaySiparisNo(analysis.animal)}`,
        `Silinecek ödeme kaydı: ${analysis.paymentCount}`,
        "",
        "Bu işlem sadece seçilen hisseyi siler. Diğer satışlar yerinde kalır.",
      ].join("\n")
    );

    if (!ok) return;

    try {
      const result = await deleteSaleOperationRequest(share.id);
      await applyOperationResult(result);
    } catch (error) {
      console.error(error);
      window.alert("Satış silinirken çakışma veya bağlantı hatası oluştu. Lütfen son veriyi kontrol edip tekrar deneyin.");
    }
  }

  function handleEditSave(payload: SaleEditPayload) {
    onEditSave(share.id, payload);
  }

  return (
    <>
      <div
        className={`bg-farm-warm border rounded-farm overflow-hidden ${
          share.kalan > 0 ? "border-farm-earth/20" : "border-farm-border"
        }`}
      >
        <div className="flex items-center justify-between px-3 py-2.5 bg-farm-cream border-b border-farm-border flex-wrap gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center text-farm-earth">
                <i className="ri-funds-box-line text-sm" />
              </div>
              <span className="font-body text-xs font-bold text-farm-brown">{kupeNo}</span>
            </div>

            <span
              className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                animalTur === "Büyükbaş"
                  ? "bg-farm-earth-pale text-farm-earth"
                  : "bg-farm-green-pale text-farm-green"
              }`}
            >
              {animalTur === "Büyükbaş" ? "B.Baş" : "K.Baş"}
            </span>

            <span className="font-body text-[10px] text-farm-muted">{share.hisseNo}</span>

            {share.hisseSayisi > 1 && (
              <span className="font-body text-[10px] bg-farm-gold-pale text-farm-gold px-1.5 py-0.5 rounded whitespace-nowrap">
                {share.hisseSayisi} hisse • Slot {slotNos.join(", ")}
              </span>
            )}

            {hisseliSatisEtiketi && (
              <span className="font-body text-[10px] bg-white text-farm-brown border border-farm-border px-1.5 py-0.5 rounded whitespace-nowrap">
                Hisseli Satış
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                finansBadge[share.durum] ?? ""
              }`}
            >
              {share.durum}
            </span>
            <span
              className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                opsColor[animalDurum]
              }`}
            >
              {animalDurum}
            </span>
          </div>
        </div>

        <div className="px-3 py-2.5 space-y-2.5">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-3 gap-y-2">
            <InfoField label="Sipariş No" value={siparisNo} />
            <InfoField label="Kesim Günü" value={kesimGunu || "—"} />
            {share.satisFisNo && <InfoField label="Fiş No" value={share.satisFisNo} />}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-x-3 gap-y-2">
            <InfoField label="Satış" value={formatTry(share.tutar)} />
            {hasFees && <InfoField label="Kesim" value={formatTry(share.kesimUcreti ?? 0)} />}
            {hasFees && (
              <InfoField label="Parçalama" value={formatTry(share.parcalamaUcreti ?? 0)} />
            )}
            {hasFees && <InfoField label="Taşıma" value={formatTry(share.tasimaUcreti ?? 0)} />}
            {(share.indirim ?? 0) > 0 && <InfoField label="İndirim" value={`-${formatTry(share.indirim ?? 0)}`} />}
            <InfoField label="Toplam" value={formatTry(toplamUcret)} />
            <InfoField label="Ödenen" value={formatTry(share.odenen)} />
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="font-body text-[10px] text-farm-muted leading-none whitespace-nowrap">
                Kalan / Bakiye
              </p>
              <p className={`font-body text-xs font-semibold leading-none whitespace-nowrap ${share.kalan < 0 ? "text-red-600" : share.kalan > 0 ? "text-farm-earth" : "text-farm-green"}`}>
                {formatTry(share.kalan)}
              </p>
            </div>
          </div>

          {share.satisNotu && (
            <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
              <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Satış Notu
              </p>
              <p className="font-body text-xs text-farm-brown whitespace-pre-wrap">{share.satisNotu}</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-0 flex-wrap">
          <button
            type="button"
            onClick={() =>
              navigate("/panel/odeme-al", {
                state: {
                  customerId,
                  selectedShareIds: [share.id],
                },
              })
            }
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-green/10 border border-farm-green/25 text-farm-green font-body text-[10px] font-semibold hover:bg-farm-green hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-cash-line text-xs" />
            </div>
            Ödeme Al
          </button>

          <button
            type="button"
            onClick={() => setShowPaymentDrawer(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-cream border border-farm-border text-farm-muted font-body text-[10px] font-semibold hover:border-farm-earth/40 hover:text-farm-earth transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xs" />
            </div>
            Ödeme Geçmişi
          </button>

          <Link
            to={`/panel/hayvanlar/${animalId}`}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth font-body text-[10px] font-semibold hover:bg-farm-earth hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-xs" />
            </div>
            Satış Detayı
          </Link>

          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-cream border border-farm-border text-farm-muted font-body text-[10px] font-semibold hover:border-farm-earth/40 hover:text-farm-earth transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-pencil-line text-xs" />
            </div>
            Düzenle
          </button>

          <button
            type="button"
            onClick={() => setShowTransferModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth font-body text-[10px] font-semibold hover:bg-farm-earth hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-share-forward-line text-xs" />
            </div>
            Başka Hayvana Aktar
          </button>

          <button
            type="button"
            onClick={handleDeleteSale}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 font-body text-[10px] font-semibold hover:bg-red-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-close-circle-line text-xs" />
            </div>
            Satışı İptal Et
          </button>
        </div>
      </div>

      {showEdit && ownerAnimal && (
        <EditPanel
          share={share}
          animal={ownerAnimal}
          kaporaTutar={kaporaPayment?.tutar ?? 0}
          kaporaTip={kaporaPayment?.tip ?? "Nakit"}
          kaporaDekontNo={kaporaPayment?.havaleNo ?? share.pesinatDekontNo ?? ""}
          kaporaAciklama={kaporaPayment?.aciklama ?? share.pesinatAciklama ?? ""}
          onSave={handleEditSave}
          onClose={() => setShowEdit(false)}
        />
      )}

      {showPaymentDrawer && (
        <SharePaymentDrawer
          share={share}
          animalId={animalId}
          onClose={() => setShowPaymentDrawer(false)}
        />
      )}

      {showTransferModal && (
        <ShareTransferModal
          share={share}
          sourceAnimalId={animalId}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </>
  );
}
